import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  Tooltip,
  Menu,
  MenuItem,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckIcon from "@mui/icons-material/Check";
import PreviewIcon from "@mui/icons-material/Preview";
import { styled } from "@mui/material/styles";
import MediaPicker from "./MediaPicker";
import {
  TypographyLarge,
  TypographyNormal,
  TypographySmall,
} from "../../customComponent";
import {
  CloseConfirmationDialog,
  openCancelDialog,
} from "../../global/CloseConfirmationDialog";
import { uploadMedia, setErrorHandler } from "../../../store/actions/cmsAction";
import { inputOnlyNumbers } from "../../../helpers/inputProcessing";
import { getImageSource, splitFileName } from "../../../helpers/fileProcessing";
import { colorStyling } from "../../../helpers/color";
import { logger } from "../../../helpers/logger";
import { v4 as uuidv4 } from "uuid";
import parser from "html-react-parser";
import UploadErrorDialog from "./UploadErrorDialog";

const assetMaxSizeLimit =
  (process.env.REACT_APP_ASSET_MAX_SIZE_LIMIT || 300) * 1024 * 1024; // 300MB

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  ":last-child": {
    marginBottom: 0,
  },
}));

const AdContentSetting = ({
  open,
  handleClose,
  addContent,
  container,
  editAsset,
  saveEditChanges,
  assetCount,
}) => {
  const dispatch = useDispatch();
  const { layerMedias, uploadingMedia, allowedAssetFiles } = useSelector(
    (state) => state.cms
  );

  const { t } = useTranslation("cms");
  const { t: tCommon } = useTranslation();

  const [adIndex, setAdIndex] = useState(null);
  const [adType, setAdType] = useState("VISTAR");
  const [adDuration, setAdDuration] = useState(10);
  const [adPlaceholderCheck, setAdPlaceholderCheck] = useState(true);
  const [adPlaceholderFile, setAdPlaceholderFile] = useState({});
  const [assetFit, setAssetFit] = useState("FIT");
  const [editedFlag, setEditedFlag] = useState(false);

  const [openMediaPicker, setOpenMediaPicker] = useState(false);
  const [errNoPlaceholder, setErrNoPlaceholder] = useState(false);
  const [errNotUploaded, setErrNotUploaded] = useState(false);

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [fileErrors, setFileErrors] = useState([]);
  const [fileErrorMessages, setFileErrorMessages] = useState([]);
  const [errUploadPlaceholder, setErrUploadPlaceholder] = useState(false);

  const [adPlaceholder, setAdPlaceholder] = useState({});
  const [showThumbnailButton, setShowThumbnailButton] = useState(false);
  const [showThumbnailFull, setShowThumbnailFull] = useState(false);

  const [anchorMenuEl, setAnchorMenuEl] = useState(null);
  const openMenu = Boolean(anchorMenuEl);
  const handleOpenMenu = (event) => {
    setAnchorMenuEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorMenuEl(null);
  };

  const handleOpenMediaPicker = () => {
    setOpenMediaPicker(true);
  };

  const handleCloseMediaPicker = () => {
    setOpenMediaPicker(false);
  };

  useEffect(() => {
    if (open) {
      setAdIndex(null);
      setAdType("VISTAR");
      setAdDuration(10);
      setAdPlaceholder({});
      setAdPlaceholderFile({});
      setAdPlaceholderCheck(true);
      setAssetFit("FIT");
      setShowThumbnailButton(false);
      setShowThumbnailFull(false);
      setErrNoPlaceholder(false);
      setErrNotUploaded(false);
      setErrUploadPlaceholder(false);

      if (editAsset) {
        const index = layerMedias[container.id].findIndex(
          (media) => media.id === editAsset.id
        );

        setAdIndex(index);
        setAdType(editAsset.type.split("_")[0]);
        setAdDuration(editAsset.interval);
        setAssetFit(editAsset.assetFit);
        if (editAsset.placeholder?.id) {
          setAdPlaceholder(editAsset.placeholder);
          setAdPlaceholderFile(editAsset.placeholder);
          setShowThumbnailButton(true);
        } else {
          setAdPlaceholderCheck(false);
        }
      }
    } else {
      setEditedFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!errorDialogOpen) {
      setFileErrors([]);
      setFileErrorMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorDialogOpen]);

  const handleCloseDialog = async () => {
    handleClose();
    setAdPlaceholderFile({});
  };

  const handleOpenCancelDialog = () => {
    openCancelDialog(dispatch, editAsset && editedFlag, handleCloseDialog);
  };

  const handleAdPlaceholderCheck = (value) => {
    setAdPlaceholderCheck(value);
    setErrNoPlaceholder(false);
    setErrNotUploaded(false);
    setErrUploadPlaceholder(false);
    setEditedFlag(true);
  };

  const handleAdPlaceholderFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const errorFiles = [];
    const errorMessages = [];
    if (file.size > assetMaxSizeLimit) {
      errorFiles.push(file);
      errorMessages.push(t("uploadError.errSizeExceedLimit"));
      setFileErrors(errorFiles);
      setFileErrorMessages(errorMessages);
      setErrorDialogOpen(true);
      return;
    }

    setShowThumbnailButton(false);
    setErrNoPlaceholder(false);
    setErrNotUploaded(false);
    setErrUploadPlaceholder(false);
    setAdPlaceholder({});
    setAdPlaceholderFile(file);
    setEditedFlag(true);
  };

  const setupAdPlaceholder = (data, order) => {
    if (order.length === 0) {
      dispatch(setErrorHandler({ selectedMedia: true }));
      return;
    }

    let placeholderFile;
    for (let i = 0; i < order.length; i++) {
      placeholderFile = data[order[i]];
      logger.log(`setupAdPlaceholder:`, placeholderFile);
      setAdPlaceholderFile(placeholderFile);
    }

    handleCloseMediaPicker();
    setShowThumbnailButton(true);
    setErrNoPlaceholder(false);
    setErrNotUploaded(false);
    setErrUploadPlaceholder(false);
    setAdPlaceholder(placeholderFile);
    setEditedFlag(true);
  };

  const uploadNewMedia = async () => {
    const payload = [];
    logger.log(`UPLOADED:`, { adDuration, adPlaceholderFile });
    if (!adPlaceholderFile.name) {
      return;
    }

    const fileNameSplit = splitFileName(adPlaceholderFile.name);
    const inputProps = {
      ...fileNameSplit,
      type: adPlaceholderFile.type,
    };

    await getImageSource(
      { input: adPlaceholderFile, inputProps },
      allowedAssetFiles
    ).then(
      ({ type, url, dimension, videoURL, localURL }) => {
        const placeholder = {
          url,
          id: uuidv4(),
          file: adPlaceholderFile,
          fileSrc: url,
          videoSrc: videoURL,
          type: type,
          name: adPlaceholderFile.name,
          dimension: dimension,
          size: adPlaceholderFile.size,
        };

        setAdPlaceholder(placeholder);
        payload.push(placeholder);

        dispatch(
          uploadMedia(payload, (res) => {
            if (res.length > 0) {
              setAdPlaceholder(res[0]);
              setErrNoPlaceholder(false);
              setErrNotUploaded(false);
              setErrUploadPlaceholder(false);
              setTimeout(() => {
                setShowThumbnailButton(true);
              }, 1234);
            }
          })
        );
      },
      ({ file, message }) => {
        logger.error(`ERROR:`, message);
        setErrUploadPlaceholder(true);
      }
    );
  };

  const handleAdType = (e) => {
    const value = e.target.value;
    setAdType(value);
    setEditedFlag(true);
  };

  const handleAdDuration = (e) => {
    const value = e.target.value;
    setAdDuration(value);
    setEditedFlag(true);
  };

  const handleAssetFit = (e) => {
    const value = e.target.value;
    setAssetFit(value);
    setEditedFlag(true);
  };

  const handleSubmitAdContent = () => {
    const shortUUID = uuidv4();
    const addedContent = {
      id: shortUUID,
      interval: parseInt(adDuration),
      appType: "ads",
      type: `${adType}_ADVERTISEMENT`,
      name: t(`adContent.name${adType}`),
      assetFit: assetFit,
    };

    if (adPlaceholderCheck) {
      addedContent["placeholder"] = adPlaceholder.response ?? adPlaceholder;
      if (adPlaceholderFile.name && !adPlaceholder.id) {
        setErrNotUploaded(true);
      }
      if (!addedContent.placeholder?.name) {
        setErrNoPlaceholder(true);
        return;
      }
    }

    logger.log(`handleSubmitAdContent:`, addedContent);
    const updatedMedias = {};
    const updatedOrder = [];
    updatedMedias[addedContent.id] = addedContent;
    updatedOrder.push(addedContent.id);
    addContent(updatedMedias, updatedOrder);
  };

  const handleSaveEdit = () => {
    const edited = {
      id: editAsset.id,
      interval: parseInt(adDuration),
      appType: "ads",
      type: `${adType}_ADVERTISEMENT`,
      name: t(`adContent.name${adType}`),
      assetFit: assetFit,
    };

    if (adPlaceholderCheck) {
      edited["placeholder"] = adPlaceholder.response ?? adPlaceholder;
      if (adPlaceholderFile.name && !adPlaceholder.id) {
        setErrNotUploaded(true);
      }
      if (!edited.placeholder?.name) {
        setErrNoPlaceholder(true);
        return;
      }
    } else {
      edited["placeholder"] = null;
    }

    logger.log(`handleSaveEdit:`, edited);
    saveEditChanges(adIndex, edited);
    handleClose();
  };

  const submitChanges = () => {
    if (!editAsset) {
      handleSubmitAdContent();
    } else {
      handleSaveEdit();
    }
  };

  const parseDialogTitle = useMemo(() => {
    if (!editAsset) {
      return t("adContent.addSettingTitle");
    } else {
      return t("adContent.editSettingTitle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAsset]);

  const parseContentSubmitText = useMemo(() => {
    if (!editAsset) {
      return tCommon("submitBtn");
    } else {
      return tCommon("saveBtn");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAsset]);

  const previewPlaceholder = useMemo(() => {
    if (!adPlaceholder?.name) {
      return null;
    }

    let placeholder = adPlaceholder;
    if (!placeholder.url && placeholder.response) {
      placeholder = placeholder.response;
    }

    const placeholderURL =
      placeholder.vidSrc ?? placeholder.url ?? placeholder.src;
    switch (placeholder.type) {
      case "VIDEO":
        return (
          <video
            is="x-muted"
            draggable={false}
            src={placeholderURL}
            alt={placeholder.name}
            controls={true}
            onClick={(e) => e.stopPropagation()}
            style={{
              minHeight: "100%",
              objectFit: "contain",
            }}
          />
        );

      case "image":
      default:
        return (
          <img
            draggable={false}
            src={placeholderURL}
            alt={t("contentImageAltText")}
            onClick={(e) => e.stopPropagation()}
            style={{
              objectFit: "contain",
            }}
          />
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adPlaceholder]);

  const enableDuration = useMemo(() => {
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetCount, editAsset]);

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={true}
      onClose={() => (!editedFlag ? handleCloseDialog() : null)}
    >
      <CloseConfirmationDialog handleCancel={handleCloseDialog} />

      <DialogTitle
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        {parseDialogTitle}
        <IconButton onClick={() => handleOpenCancelDialog()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ minHeight: "33vh" }}>
        <UploadErrorDialog
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          files={fileErrors}
          errors={fileErrorMessages}
        />

        <MediaPicker
          open={openMediaPicker}
          handleClose={handleCloseMediaPicker}
          name={``}
          addContent={setupAdPlaceholder}
          container={container}
          contentState={[null]}
          onlyOne={true}
          noUpload={true}
        />

        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          sx={{ mt: 3, px: 3 }}
        >
          <Box
            display={"flex"}
            alignItems={"center"}
            sx={{ p: 4, width: "100%" }}
          >
            <Box
              display={"flex"}
              justifyContent={"left"}
              alignItems={"center"}
              sx={{
                py: 2,
                px: 4,
                width: "100%",
                border: "1px solid black",
                borderRadius: "22px",
              }}
            >
              <Box
                width={"70%"}
                display={"flex"}
                flexDirection={"column"}
                gap={1}
              >
                <StyledBox>
                  <TypographyLarge sx={{ ml: 1 }}>
                    {t("adContent.adTypeLabel")}
                  </TypographyLarge>
                  <RadioGroup
                    row
                    value={adType}
                    onChange={(e) => handleAdType(e)}
                    sx={{ ml: 2 }}
                  >
                    <FormControlLabel
                      value={"VISTAR"}
                      control={<Radio size="small" />}
                      label={t("adContent.adTypeVistar")}
                      slotProps={{ typography: { sx: { fontSize: "14px" } } }}
                    />
                    <FormControlLabel
                      value={"HIVESTACK"}
                      control={<Radio size="small" />}
                      label={t("adContent.adTypeHivestack")}
                      slotProps={{ typography: { sx: { fontSize: "14px" } } }}
                    />
                  </RadioGroup>
                </StyledBox>

                <StyledBox>
                  <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                    <TypographyLarge sx={{ ml: 1 }}>
                      {t("adContent.durationAdLabel")}
                    </TypographyLarge>
                    <Tooltip
                      arrow
                      disableInteractive
                      placement="top"
                      title={
                        <TypographySmall
                          sx={{ color: "white", textAlign: "center" }}
                        >
                          {t("adContent.durationAdTip")}
                        </TypographySmall>
                      }
                    >
                      <InfoOutlinedIcon fontSize="10px" />
                    </Tooltip>
                  </Box>

                  <Box
                    width={"50%"}
                    display={"flex"}
                    alignItems={"center"}
                    gap={1}
                  >
                    <Tooltip
                      arrow
                      disableInteractive
                      title={
                        enableDuration ? (
                          ""
                        ) : (
                          <TypographySmall
                            sx={{ color: "white", textAlign: "center" }}
                          >
                            {t("setting.durationDisabledTip")}
                          </TypographySmall>
                        )
                      }
                    >
                      <TextField
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={adDuration}
                        disabled={!enableDuration}
                        onChange={(e) => handleAdDuration(e)}
                        onKeyDown={(e) => inputOnlyNumbers(e)}
                        sx={{ pl: 2 }}
                      />
                    </Tooltip>
                    <TypographyNormal flex={"none"}>
                      {t("setting.durationHint")}
                    </TypographyNormal>
                  </Box>
                </StyledBox>

                <Box display={"flex"} alignItems={"center"}>
                  <Checkbox
                    value={adPlaceholderCheck}
                    checked={adPlaceholderCheck}
                    onClick={() =>
                      handleAdPlaceholderCheck(!adPlaceholderCheck)
                    }
                    sx={{ padding: "4px" }}
                  />
                  <InputLabel
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      handleAdPlaceholderCheck(!adPlaceholderCheck)
                    }
                  >
                    <TypographyLarge>
                      {parser(t("setting.placeholderCheckbox"))}
                    </TypographyLarge>
                  </InputLabel>
                </Box>

                {adPlaceholderCheck && (
                  <Box display={"flex"} alignItems={"center"}>
                    <input
                      accept="image/*,video/*"
                      style={{ display: "none" }}
                      id="file-upload"
                      type="file"
                      onChange={handleAdPlaceholderFileChange}
                    />
                    <Box sx={{ ml: 2 }}>
                      <Button
                        variant="contained"
                        component="span"
                        onClick={handleOpenMenu}
                        sx={{
                          mr: 1,
                          "&:hover": {
                            backgroundColor: "#0093ff",
                          },
                        }}
                      >
                        {tCommon("chooseBtn")}
                      </Button>
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorMenuEl}
                        open={openMenu}
                        onClose={handleCloseMenu}
                        elevation={1}
                        anchorOrigin={{
                          horizontal: "left",
                          vertical: "bottom",
                        }}
                        transformOrigin={{
                          horizontal: "left",
                          vertical: "top",
                        }}
                        sx={{ marginTop: 1 }}
                      >
                        <label htmlFor="file-upload">
                          <MenuItem
                            onClick={() => {
                              handleCloseMenu();
                            }}
                            sx={{
                              display: "flex",
                              ":hover": {
                                color: colorStyling.white.hover,
                                backgroundColor: colorStyling.primary,
                              },
                            }}
                          >
                            <TypographyNormal sx={{ ml: 1, color: "inherit" }}>
                              {t("setting.placeholderUploadNew")}
                            </TypographyNormal>
                          </MenuItem>
                        </label>

                        <MenuItem
                          onClick={() => {
                            handleOpenMediaPicker();
                            handleCloseMenu();
                          }}
                          sx={{
                            display: "flex",
                            ":hover": {
                              color: colorStyling.white.hover,
                              backgroundColor: colorStyling.primary,
                            },
                          }}
                        >
                          <TypographyNormal sx={{ ml: 1, color: "inherit" }}>
                            {t("setting.placeholderExistingMedia")}
                          </TypographyNormal>
                        </MenuItem>
                      </Menu>
                    </Box>

                    <TextField
                      variant="outlined"
                      value={adPlaceholderFile.name ?? ""}
                      placeholder={t("setting.placeholderPlaceholder")}
                      disabled
                      sx={{
                        // for future reference
                        "& .MuiOutlinedInput-root": {},
                        "& .MuiInputBase-input.Mui-disabled": {
                          py: 0,
                          height: "40px",
                          WebkitTextFillColor: colorStyling.black,
                          backgroundColor: colorStyling.white.inactive,
                          border: errNoPlaceholder && "1px solid red",
                        },
                      }}
                    />

                    <Box
                      display={"flex"}
                      gap={2}
                      alignItems={"center"}
                      sx={{ width: "123px", ml: 2 }}
                    >
                      {!showThumbnailButton && (
                        <>
                          {!uploadingMedia[adPlaceholder.id] ? (
                            <Box>
                              {adPlaceholderFile?.name && (
                                <Button
                                  variant="contained"
                                  sx={{ ml: 2, bgcolor: "#1976d2" }}
                                  onClick={uploadNewMedia}
                                >
                                  {tCommon("uploadBtn")}
                                </Button>
                              )}
                            </Box>
                          ) : (
                            <Box
                              width={60}
                              minHeight={5}
                              sx={{
                                mt: "2px",
                                position: "relative",
                              }}
                            >
                              {uploadingMedia[adPlaceholder.id].status ===
                              "queued" ? (
                                <LinearProgress
                                  sx={{
                                    height: 5,
                                    borderRadius: "11px",
                                  }}
                                  variant="query"
                                />
                              ) : (
                                <LinearProgress
                                  sx={{
                                    height: 5,
                                    borderRadius: "11px",
                                  }}
                                  variant="determinate"
                                  value={
                                    uploadingMedia[adPlaceholder.id].progress
                                  }
                                />
                              )}

                              {uploadingMedia[adPlaceholder.id].status ===
                                "completed" && (
                                <Box
                                  display="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                  sx={{
                                    p: "1px",
                                    top: "-16px",
                                    right: "-11px",
                                    position: "absolute",
                                    borderRadius: "999px",
                                    backgroundColor: "powderblue",
                                  }}
                                >
                                  <CheckIcon fontSize="small" />
                                </Box>
                              )}
                            </Box>
                          )}
                        </>
                      )}

                      {showThumbnailButton && (
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<PreviewIcon />}
                          onClick={() => setShowThumbnailFull(true)}
                          sx={{
                            mr: 1,
                            color: "#1976d2",
                            borderColor: "#1976d2",
                            backgroundColor: "white",
                            "&:hover": {
                              backgroundColor: "#e3f2fd",
                            },
                          }}
                        >
                          {tCommon("previewBtn")}
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}

                {errNoPlaceholder && (
                  <TypographySmall
                    sx={{
                      ml: 1,
                      mt: "2px",
                      color: "red",
                      fontWeight: 600,
                    }}
                  >
                    {errNotUploaded
                      ? t("setting.errNoPlaceholderNotUploaded")
                      : t("setting.errNoPlaceholderNoFile")}
                  </TypographySmall>
                )}

                {errUploadPlaceholder && (
                  <TypographySmall
                    sx={{
                      ml: 1,
                      mt: "2px",
                      color: "red",
                      fontWeight: 600,
                    }}
                  >
                    {t("setting.errUploadPlaceholder")}
                  </TypographySmall>
                )}

                <StyledBox>
                  <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                    <TypographyLarge sx={{ ml: 1 }}>
                      {t("setting.assetBehaviorLabel")}
                    </TypographyLarge>
                    <Tooltip
                      arrow
                      disableInteractive
                      placement="top"
                      title={
                        <TypographySmall
                          sx={{ color: "white", textAlign: "center" }}
                        >
                          {t("setting.assetBehaviorTip")}
                        </TypographySmall>
                      }
                    >
                      <InfoOutlinedIcon fontSize="10px" />
                    </Tooltip>
                  </Box>

                  <Box width={"70%"} display={"flex"} alignItems={"center"}>
                    <TextField
                      margin="dense"
                      fullWidth
                      variant="outlined"
                      size="small"
                      select
                      SelectProps={{
                        native: true,
                      }}
                      value={assetFit}
                      onChange={(e) => handleAssetFit(e)}
                      sx={{ pl: 2 }}
                    >
                      <option value={"FIT"} key={"fit"}>
                        {t("setting.assetBehaviorFit")}
                      </option>
                      <option value={"STRETCH"} key={"stretch"}>
                        {t("setting.assetBehaviorStretch")}
                      </option>
                    </TextField>
                  </Box>
                </StyledBox>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ mx: 2, mb: 1 }}>
        <>
          <Button variant="outlined" onClick={() => handleOpenCancelDialog()}>
            {tCommon("cancelBtn")}
          </Button>
          <Button variant="contained" onClick={() => submitChanges()}>
            {parseContentSubmitText}
          </Button>
        </>
      </DialogActions>

      <Dialog
        open={showThumbnailFull}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={true}
        onClose={() => setShowThumbnailFull(false)}
        PaperProps={{ sx: { backgroundColor: "black" } }}
      >
        {previewPlaceholder}
      </Dialog>
    </Dialog>
  );
};

export default AdContentSetting;
